import { ResponseError } from '@dropbox/sign-internal-client';
import { useQuery } from '@tanstack/react-query';
import { ErrorWithContext } from 'common/utils/sentry/error-with-context';
import invariant from 'invariant';
import { useHistory } from 'react-router';
import { useSignerAppClient } from 'signer-app/context/signer-app-client';
import { populateFieldValues } from 'signer-app/signer-experience/saved-data-utils';
import { SignatureRequestResponse } from 'signer-app/signer-experience/types';
import { useBundleProps } from 'signer-app/bundle-props';
import { redirectTo } from 'signer-app/utils/redirect';
import { getEidRedirect } from './get-eid-redirect';
import { responseToSentryContext } from './response-to-sentry-context';
import { signerExperienceQueryKey } from './types';
import type { AccessCodeResponse } from './use-signer-auth';

type UseFetchSignatureRequestParams = {
  signatureRequestId: string;
  accessCode: string | null;
  isReady: boolean;
};

export function useFetchSignatureRequest({
  signatureRequestId,
  accessCode,
  isReady,
}: UseFetchSignatureRequestParams) {
  const client = useSignerAppClient();
  const history = useHistory();
  const { tsmGuid } = useBundleProps();

  const queryKey = signerExperienceQueryKey(signatureRequestId, accessCode);

  const { data, isFetching } = useQuery<
    SignatureRequestResponse | AccessCodeResponse
  >({
    queryKey,
    enabled: isReady,
    useErrorBoundary() {
      return true;
    },
    keepPreviousData: true,
    queryFn: async (): Promise<
      SignatureRequestResponse | AccessCodeResponse
    > => {
      try {
        const response =
          await client.signerApi.signerAppSignatureRequestGetRequest(
            signatureRequestId,
            accessCode ?? undefined,
            tsmGuid ?? undefined,
          );

        const eidRedirect = getEidRedirect(
          signatureRequestId,
          response.qualifiedSignatureProperties ?? {},
        );
        if (eidRedirect) {
          history.push(eidRedirect);
          return response;
        }

        invariant(
          response.signatureRequest.pages.length > 0,
          'No pages found - invalid signature request',
        );
        invariant(
          response.signatureRequest.fields.length > 0,
          'No pages found - invalid signature request',
        );

        return {
          ...response,
          signatureRequest: {
            ...response.signatureRequest,
            fields: populateFieldValues(response.signatureRequest.fields, {
              defaultDateFormat: response.defaultDateFormat,
            }),
          },
        };
      } catch (error) {
        if ((error as Error).name === 'ResponseError') {
          const response = (error as ResponseError).response;
          const status = response.status;

          switch (status) {
            case 403:
              if (!accessCode) {
                return 'need-access-code' as const;
              }
              return 'invalid-access-code' as const;
            case 429:
              return 'too-many-retries' as const;
            case 410:
              await redirectTo('/info/externalNothingToSign');
              break;
            case 401:
              await redirectTo(
                `/account/logIn?on_login_redirect_url=${encodeURIComponent(location.href)}`,
              );
              break;
            default: {
              throw new ErrorWithContext(
                `Unexpected ResponseError HTTP ${status}`,
                {
                  tags: {
                    errorType: 'signerAppGetFailure',
                    httpStatus: status,
                  },
                  contexts: {
                    response: await responseToSentryContext(response),
                    request: {
                      signatureRequestId,
                      tsmGuid,
                      hasAccessCode: !!accessCode,
                    },
                  },
                },
              );
            }
          }
        }
        throw error;
      }
    },
  });

  const signatureRequest =
    typeof data === 'string' ? undefined : data?.signatureRequest;

  return {
    data,
    isFetching,
    signatureRequest,
    queryKey,
  };
}
